import React, { useState, useMemo, useEffect } from "react";

const ServiceFormsTable = ({ forms = [], onSelectForm, rowsPerPage = 10 }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    const totalEntries = forms.length;
    const totalPages = Math.ceil(totalEntries / rowsPerPage);

    const sortedForms = useMemo(() => {
        if (!sortConfig.key) return [...forms];
        const sorted = [...forms].sort((a, b) => {
            let aVal = a[sortConfig.key] ?? "";
            let bVal = b[sortConfig.key] ?? "";

            if (sortConfig.key.toLowerCase().includes("date")) {
                aVal = aVal ? new Date(aVal) : new Date(0);
                bVal = bVal ? new Date(bVal) : new Date(0);
                return aVal - bVal;
            }

            return aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true });
        });
        return sortConfig.direction === "asc" ? sorted : sorted.reverse();
    }, [forms, sortConfig]);

    const paginatedForms = sortedForms.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [forms]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return "";
        return sortConfig.direction === "asc" ? " ▲" : " ▼";
    };

    const from = (currentPage - 1) * rowsPerPage + 1;
    const to = Math.min(currentPage * rowsPerPage, totalEntries);

    return (
        <div className="service-form-section-content">
            <div className="paginated-table-container">
                <table className="paginated-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort("displayName")} style={{ cursor: "pointer" }}>
                                Form Name{getSortIndicator("displayName")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedForms.map((form, idx) => (
                            <tr key={idx}>
                                <td
                                    className="paginated-table-row-clickable"
                                    onClick={() => onSelectForm && onSelectForm(form)}
                                >
                                    {form.displayName}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="pagination-bar">
                    <div className="pagination-summary">
                        Showing {from}–{to} of {totalEntries} entries
                    </div>

                    <div className="pagination-controls">
                        <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
                            «
                        </button>
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                            ‹
                        </button>

                        {[...Array(totalPages)].map((_, i) => {
                            const page = i + 1;
                            if (page < currentPage - 1 || page > currentPage + 1) return null;

                            return (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    className={currentPage === page ? "active" : ""}
                                >
                                    {page}
                                </button>
                            );
                        })}

                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                            ›
                        </button>
                        <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
                            »
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceFormsTable;
